// src/context/DoorloopjesContext.js

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { wisToken } from '../utils/videoVerificatie';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;
const DoorloopjesContext = createContext();

export function useDoorloopjes() {
  return useContext(DoorloopjesContext);
}

export function DoorloopjesProvider({ children }) {
  const [doorloopjes, setDoorloopjes] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [globalSettings, setGlobalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  
  // Tour state (Joyride)
  const [tourState, setTourState] = useState({ run: false, stepIndex: 0 });
  const startTourTimeoutRef = useRef(null);

  // Actieve gesprek-id van de AI-assistent. De chatwidget vraagt dit op via
  // getConversationId; een ref zodat een wissel direct zichtbaar is, zonder te
  // wachten op een render. lastAiSessionWriteRef voorkomt dat een verversing van
  // de gebruiker (bij focus) een net gekozen gesprek terugzet naar de oude waarde
  // terwijl het opslaan nog loopt.
  const aiSessionIdRef = useRef(null);
  const lastAiSessionWriteRef = useRef(0);

  // Het token waarmee de gebruiker, de doorloopjes en de instellingen hierboven
  // geladen zijn (null: zonder token). Staat er een ander token in localStorage
  // (inloggen in dit of een ander tabblad, een pagina terug uit de cache van de
  // browser), dan hoort de data bij een andere sessie en laadt syncWithToken alles
  // opnieuw.
  const loadedTokenRef = useRef(undefined);

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    const jwt = localStorage.getItem('jwt');
    loadedTokenRef.current = jwt;
    let userPromise = Promise.resolve({ data: null });
    let doorloopjesPromise = Promise.resolve({ data: { data: [] } });
    const globalSettingsPromise = axios.get(
      `${STRAPI_URL}/api/global-setting?populate[0]=logo&populate[1]=dashboardLogo&populate[2]=poweredByLogo`
    );

    if (jwt) {
      const populateUserQuery =
        'populate[favorite_doorloopjes]=*' +
        '&populate[completed_doorloopjes]=*' +
        '&populate[inprogress_doorloopjes]=*';

      userPromise = axios.get(`${STRAPI_URL}/api/users/me?${populateUserQuery}`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });

      doorloopjesPromise = axios.get(
        `${STRAPI_URL}/api/doorloopjes` +
          `?sort[0]=book:asc&sort[1]=page:asc` +
          `&pagination[pageSize]=250` +
          `&populate[thumbnail][fields][0]=url&populate[thumbnail][fields][1]=formats` +
          `&populate[theme][fields][0]=slug&populate[theme][fields][1]=title&populate[theme][fields][2]=description`,
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
    }

    try {
      const [globalSettingsRes, userRes, doorloopjesRes] = await Promise.all([
        globalSettingsPromise,
        userPromise,
        doorloopjesPromise
      ]);
      // Intussen is een lading met een ander token begonnen; die vult de data.
      if (loadedTokenRef.current !== jwt) return;

      setGlobalSettings(globalSettingsRes.data.data);
      setCurrentUser(userRes.data);
      aiSessionIdRef.current = userRes.data?.ai_session_id || null;
      setDoorloopjes(doorloopjesRes.data.data);
      setConnectionError(false);
      setLoading(false);

    } catch (error) {
      if (loadedTokenRef.current !== jwt) return;
      console.error("Fout bij ophalen initiële data:", error.response?.data || error.message);
      if (error.response?.status === 401 || error.response?.status === 403) {
        // Alleen het afgewezen token wissen, niet een token van een login die
        // intussen in een ander tabblad gebeurde.
        if (localStorage.getItem('jwt') === jwt) {
          localStorage.removeItem('jwt');
          wisToken();
        }
        loadedTokenRef.current = null;
      } else if (!error.response) {
        // Geen response = backend onbereikbaar (network error / timeout)
        setConnectionError(true);
      }
      setGlobalSettings(null);
      setCurrentUser(null);
      setDoorloopjes([]);
      setLoading(false);
    }
  }, []);

  // Haalt de gebruiker opnieuw op zonder de algemene 'loading' state te triggeren,
  // zodat dit tabblad de laatste ai_session_id heeft als het weer actief wordt.
  const refreshUser = useCallback(async () => {
    const jwt = localStorage.getItem('jwt');

    if (!jwt) return;

    try {
      const populateUserQuery =
        'populate[favorite_doorloopjes]=*' +
        '&populate[completed_doorloopjes]=*' +
        '&populate[inprogress_doorloopjes]=*';

      const userRes = await axios.get(`${STRAPI_URL}/api/users/me?${populateUserQuery}`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      
      // Update alleen als er data is
      if (userRes.data) {
        // Een ander tabblad kan het actieve gesprek gewisseld hebben.
        if (Date.now() - lastAiSessionWriteRef.current > 10_000) {
          aiSessionIdRef.current = userRes.data.ai_session_id || null;
        }
        // Alleen updaten als de data daadwerkelijk anders is om onnodige renders te voorkomen
        setCurrentUser(prev => {
            if (JSON.stringify(prev) !== JSON.stringify(userRes.data)) {
                return userRes.data;
            }
            return prev;
        });
      }
    } catch (error) {
      console.error("Fout bij verversen gebruiker (refreshUser):", error);
    }
  }, []);

  // Bij een ander token dan dat van de geladen data alles opnieuw laden, anders
  // alleen de gebruiker verversen.
  const syncWithToken = useCallback(() => {
    if (localStorage.getItem('jwt') !== loadedTokenRef.current) {
      loadInitialData();
    } else {
      refreshUser();
    }
  }, [loadInitialData, refreshUser]);

  // visibilitychange: tab switches in hetzelfde venster
  // focus: klikken tussen twee vensters (side-by-side / dual monitor)
  // pageshow met persisted: een pagina die terugkomt uit de back/forward-cache
  useEffect(() => {
    const handleActivity = () => {
      if (document.visibilityState === 'visible') {
        syncWithToken();
      }
    };
    const handlePageShow = (event) => {
      if (event.persisted) syncWithToken();
    };

    document.addEventListener('visibilitychange', handleActivity);
    window.addEventListener('focus', handleActivity);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleActivity);
      window.removeEventListener('focus', handleActivity);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [syncWithToken]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const toggleFavorite = async (doorloopjeId) => {
    const jwt = localStorage.getItem('jwt');
    if (!currentUser || !jwt) return;
    const favoriteIds = currentUser.favorite_doorloopjes.map(d => d.id);
    const isCurrentlyFavorite = favoriteIds.includes(doorloopjeId);
    const updatedFavorites = isCurrentlyFavorite
      ? currentUser.favorite_doorloopjes.filter(d => d.id !== doorloopjeId)
      : [...currentUser.favorite_doorloopjes, { id: doorloopjeId }];
    const updatedFavoriteIds = updatedFavorites.map(d => d.id);

    // Optimistic update
    setCurrentUser(prev => ({ ...prev, favorite_doorloopjes: updatedFavorites }));

    try {
      await axios.put(`${STRAPI_URL}/api/users/${currentUser.id}`,
        { favorite_doorloopjes: updatedFavoriteIds },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
    } catch (error) {
      console.error("Fout bij bijwerken favorieten:", error);
      // Rollback on error
      setCurrentUser(prev => ({ ...prev, favorite_doorloopjes: currentUser.favorite_doorloopjes }));
    }
  };

  const setCompletionState = async (doorloopjeId, newState) => {
    const jwt = localStorage.getItem('jwt');
    if (!currentUser || !jwt) return;

    const prevInProgress = currentUser.inprogress_doorloopjes || [];
    const prevCompleted = currentUser.completed_doorloopjes || [];

    let updatedInProgress = prevInProgress.filter(d => d.id !== doorloopjeId);
    let updatedCompleted = prevCompleted.filter(d => d.id !== doorloopjeId);

    if (newState === 'in_progress') {
      updatedInProgress = [...updatedInProgress, { id: doorloopjeId }];
    } else if (newState === 'completed') {
      updatedCompleted = [...updatedCompleted, { id: doorloopjeId }];
    }

    // Optimistic update
    setCurrentUser(prev => ({
      ...prev,
      inprogress_doorloopjes: updatedInProgress,
      completed_doorloopjes: updatedCompleted,
    }));

    try {
      await axios.put(
        `${STRAPI_URL}/api/users/${currentUser.id}`,
        {
          inprogress_doorloopjes: updatedInProgress.map(d => d.id),
          completed_doorloopjes: updatedCompleted.map(d => d.id),
        },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
    } catch (error) {
      console.error("Fout bij bijwerken van de voltooiingsstatus:", error);
      // Rollback on error
      setCurrentUser(prev => ({
        ...prev,
        inprogress_doorloopjes: prevInProgress,
        completed_doorloopjes: prevCompleted,
      }));
    }
  };

  // Functie om de AI sessie ID op te slaan in Strapi
  const updateAiSessionId = async (sessionId) => {
    const jwt = localStorage.getItem('jwt');
    if (!currentUser || !jwt) return;

    // De widget meldt het id bij elk bericht; alleen een wijziging opslaan.
    if ((sessionId || null) === aiSessionIdRef.current) return;
    aiSessionIdRef.current = sessionId || null;
    lastAiSessionWriteRef.current = Date.now();

    // Optimistische update in lokale state
    setCurrentUser(prev => ({ ...prev, ai_session_id: sessionId }));

    try {
      await axios.put(
        `${STRAPI_URL}/api/users/${currentUser.id}`,
        { ai_session_id: sessionId },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
    } catch (error) {
      console.error("Fout bij opslaan AI sessie ID:", error);
    }
  };

  const getActiveAiSessionId = useCallback(() => aiSessionIdRef.current, []);

  // Wisselt het gesprek in de chatwidget. De widget controleert bij het event
  // 'online' (ook bij focus en elke 30 seconden) of getConversationId iets anders
  // teruggeeft en laadt dan dat gesprek; null start een nieuw gesprek. Bewust niet
  // 'focus': daarop ververst deze context ook de gebruiker.
  const switchAiSession = (sessionId) => {
    updateAiSessionId(sessionId);
    window.dispatchEvent(new Event('online'));
  };

  // Video-analyse consent opslaan
  const giveVideoAnalysisConsent = async () => {
    const jwt = localStorage.getItem('jwt');
    if (!currentUser || !jwt) return;

    const now = new Date().toISOString();
    setCurrentUser(prev => ({ ...prev, video_analysis_consent_at: now }));

    try {
      await axios.put(
        `${STRAPI_URL}/api/users/${currentUser.id}`,
        { video_analysis_consent_at: now },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
    } catch (error) {
      console.error("Fout bij opslaan video-analyse consent:", error);
      setCurrentUser(prev => ({ ...prev, video_analysis_consent_at: null }));
    }
  };

  // ---- Tour controls ----
  const stopTour = useCallback(() => {
    if (startTourTimeoutRef.current) {
      clearTimeout(startTourTimeoutRef.current);
      startTourTimeoutRef.current = null;
    }
    setTourState({ run: false, stepIndex: 0 });
  }, []);

  const startTour = useCallback(() => {
    if (startTourTimeoutRef.current) {
      clearTimeout(startTourTimeoutRef.current);
      startTourTimeoutRef.current = null;
    }
    setTourState({ run: false, stepIndex: 0 });
    startTourTimeoutRef.current = setTimeout(() => {
      setTourState({ run: true, stepIndex: 0 });
      startTourTimeoutRef.current = null;
    }, 10);
  }, []);

  const markTourAsCompleted = async () => {
    const jwt = localStorage.getItem('jwt');
    if (!currentUser || !jwt || currentUser.has_completed_tour) return;
    try {
      await axios.put(
        `${STRAPI_URL}/api/users/${currentUser.id}`,
        { has_completed_tour: true },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      setCurrentUser(prevUser => ({ ...prevUser, has_completed_tour: true }));
    } catch (error) {
      console.error("Fout bij bijwerken van tour status:", error);
    }
  };

  // Markeert de eenmalige school-prompt als gezien (optie a: zodra hij verschijnt).
  const dismissSchoolPrompt = async () => {
    const jwt = localStorage.getItem('jwt');
    if (!currentUser || !jwt || currentUser.schoolPromptDismissed) return;
    try {
      await axios.put(
        `${STRAPI_URL}/api/users/${currentUser.id}`,
        { schoolPromptDismissed: true },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      setCurrentUser(prev => ({ ...prev, schoolPromptDismissed: true }));
    } catch (error) {
      console.error("Fout bij bijwerken schoolPromptDismissed:", error);
    }
  };

  // Koppelt een school (BRIN) aan de gebruiker vanuit de prompt.
  const linkSchool = async ({ brin, schoolName, city, opleiding }) => {
    const jwt = localStorage.getItem('jwt');
    if (!currentUser || !jwt) return;
    try {
      await axios.put(
        `${STRAPI_URL}/api/users/${currentUser.id}`,
        { brin, schoolName, city, opleiding, schoolPromptDismissed: true },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      setCurrentUser(prev => ({ ...prev, brin, schoolName, city, opleiding, schoolPromptDismissed: true }));
    } catch (error) {
      console.error("Fout bij koppelen school:", error);
    }
  };

  const value = {
    doorloopjes,
    currentUser,
    globalSettings,
    connectionError,
    toggleFavorite,
    setCompletionState,
    updateAiSessionId,
    getActiveAiSessionId,
    switchAiSession,
    giveVideoAnalysisConsent,
    loading,
    loadInitialData,
    tourState,
    setTourState,
    startTour,
    stopTour,
    markTourAsCompleted,
    dismissSchoolPrompt,
    linkSchool,
  };

  return (
    <DoorloopjesContext.Provider value={value}>
      {children}
    </DoorloopjesContext.Provider>
  );
}